import { Injectable } from '@angular/core';
import {
	PluginType,
	PluginDetectiveCaseStudyGameData,
	DetectiveParagraph,
	InteractiveElement,
	MultiChoiceGameData,
} from '@meta-press/shared-plugins';
import {
	GenerationRequest,
	GenerationResult,
	GenerationPrompts,
	ValidationResult,
} from './base/game-generation.interface';
import { BaseGenerationService } from './base/base-generation.service';
import { SettingsService } from '../settings-service/settings.service';

/**
 * Detective case study specific generation service
 */
@Injectable({
	providedIn: 'root',
})
export class DetectiveCaseStudyGenerationService
	extends BaseGenerationService<PluginDetectiveCaseStudyGameData>
{
	readonly pluginType = PluginType.DetectiveCaseStudy;

	/**
	 * Create system and user prompts for detective case study generation
	 * @param request The generation request
	 * @param settingsService Settings service for language preferences
	 * @returns System and user prompts
	 */
	createPrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts {
		const { parameters, sourceMaterial } = request;

		let systemPrompt: string;
		let userPrompt: string;

		// Get target language prompt from settings service
		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();

		// Get target difficulty prompt from settings service
		const targetDifficultyPrompt =
			settingsService.GetTargetDifficultyPrompt();

		if (sourceMaterial && sourceMaterial.fileHandles.length > 0) {
			// Source material based generation
			systemPrompt = `Olet asiantuntijakirjoittaja, joka on erikoistunut interaktiivisiin etsiväpeli-narratiiveihin. Tehtäväsi on luoda etsiväpeli-tapaustutkimus opetuspeliä varten lähdemateriaalin perusteella. Muotoile vastauksesi validina JSON-muotona.

`;

			userPrompt =
				`Luo minulle etsivätapaustutkimus mysteeri. Luo mysteeri lähdemateriaalista käyttäen hakutyökalua.
      
      Tapauksen tulee olla opettavainen, mielenkiintoinen ja sopiva opiskelijoille. Mysteeriin tulee sisältyä tutkintaa, johtolankojen keräämistä ja todisteiden etsimistä.
      
      Ole hyvä ja tarjoa:
      1. Otsikko mysteerille
      2. Lyhyt kuvaus mysteerin tavoitteesta
      3. Päädokumentin teksti, joka kuvailee tapauksen, jota pelaaja selvittää. Tutustu lähdemateriaaliin file_search-työkalulla, selvitä, tunnista materiaalista keskeinen teema, poimi siitä käänteet ja kiinnostavat tiedot. Tee tämän pohjalta poliisin tapauskertomusta muistuttava teksti.
      4. Poimi tekstistä valitsemasi sanat tai lauseet interaktiivisiksi elementeiksi. Tähän on käytettävissä kolmea erilaista elementtityyppiä: PUZZLE_TYPES.EXAMINE, PUZZLE_TYPES.CENSORED.
      
      Examine -tyyppi antaa lisätietoa lauseesta klikkaamalla. Censored on mustattu teksti, joka tulee näkyviin klikkaamalla. Plugin tenttaa käyttäjältä tietoa siitä aiheesta, jota hän on juuri klikannut. Valitse siis klikattavat sanat tai lauseet huolellisesti, jotta ne ovat merkityksellisiä mysteerin kannalta ja käyttävät valittua interaktiivista elementtiä hyödykseen strategisesti oikealla tavalla. Jos luot kysymyksen (plugin), tee vain yksi kysymys ja tee siitä niin helppo, että siihen on mahdollista vastata document-tekstin perusteella. Kysymys ei saa sisältää tietoa, jota document-tekstissä ei ole kysymykseen mennessä kerrottu. Älä ole myöskään liian tekninen kysymyksessä vaan kysy selkeitä asioita, jotka on mahdollista muistaa helposti.
      
      6. Ratkaisuteksti, joka tiivistää mysteerin ratkaisun
      
      Muotoile vastauksesi JSON-objektina seuraavalla rakenteella:
      {
        "title": "merkkijono",
        "mystery": "merkkijono",
        "document": [
          {
            "type": "paragraph",
            "text": "tutki lähdemateriaalia file_search-työkalulla, poimi keskeinen teema ja kirjoita tietoa hyväksi käyttävä etsivä-tyylinen tapauskertomus"
          }
        ],
        "clues": {
          "clue_id1": "johtolangan teksti 1",
          "clue_id2": "johtolangan teksti 2"
        },
        // Array of interactive elements. Leave empty for now.
        "interactiveElements": [
        {
            id: string;
            type: string; // 'examine' | 'censored' | 'plugin'
            word: string; // word or phrase to be interactive. Important: This word or sentence MUST exist in the document array text exactly as it is.
            clueId?: string; // Which clue this element reveals?
			pluginType?: PluginType; // If type is PLUGIN, this indicates the plugin type. Currently only choice is: 120 - use this, if you create pluginData.
            pluginData?: object; // If type is PLUGIN, this contains an object of plugin data. The object structure is:
            ` +
				new MultiChoiceGameData().getStructureDefinition() +
				`
	    }],
        "solutionClueCount": 5,
        "solutionText": "Lopullinen ratkaisuteksti, joka selittää mysteerin ratkaisun"
      }
        
      <all-clues>${parameters['existingClues'] || {}}</all-clues>
      
      Varmista, että mukana on:
      - Useita interaktiivisia elementtejä käyttäen yllä annettuja vaihtoehtoja
      - Sekoitus sensuroitua tekstiä, joka paljastaa johtolankoja napsautettaessa
      - Kysymyksiä, jotka testaavat pelaajan ymmärrystä tapauksesta
      - Johdonmukainen mysteeri, joka on opettavainen ja mukaansatempaava
      
      Interaktiivisten elementtien tulee olla oikein muotoiltuja asianmukaisilla HTML-attribuuteilla.
      
      ${targetLanguagePrompt}
      ${targetDifficultyPrompt}
      `;
		} else {
			// Topic-based generation
			const caseTitle =
				parameters['caseTitle'] ||
				parameters['questionTopic'] ||
				'Etsivätapaus';

			systemPrompt = `Olet asiantuntijakirjoittaja, joka on erikoistunut interaktiivisiin etsiväpeli-narratiiveihin. Tehtäväsi on luoda etsiväpeli-tapaustutkimus opetuspeliä varten annetun aiheen perusteella. Käytä aiheeseen liittyvää yleistietoa ja faktoja luodaksesi mielenkiintoisen etsivätapauksen. Muotoile vastauksesi validina JSON-muotona.

`;

			userPrompt = `Luo minulle etsivätapaustutkimus mysteeri aiheesta "${caseTitle}".
      
      Tapauksen tulee olla opettavainen, mielenkiintoinen ja sopiva opiskelijoille. Mysteeriin tulee sisältyä tutkintaa, johtolankojen keräämistä ja todisteiden etsimistä.
      
      Ole hyvä ja tarjoa:
      1. Otsikko mysteerille
      2. Lyhyt kuvaus mysteerin tavoitteesta
      3. Päädokumentin teksti, joka kuvailee tapauksen, jota pelaaja selvittää. Tutustu lähdemateriaaliin file_search-työkalulla, selvitä, tunnista materiaalista keskeinen teema, poimi siitä käänteet ja kiinnostavat tiedot. Tee tämän pohjalta poliisin tapauskertomusta muistuttava teksti.
      4. Poimi tekstistä valitsemasi sanat tai lauseet interaktiivisiksi elementeiksi. Tähän on käytettävissä kolmea erilaista elementtityyppiä: PUZZLE_TYPES.EXAMINE, PUZZLE_TYPES.CENSORED.
      
      Examine -tyyppi antaa lisätietoa lauseesta klikkaamalla. Censored on mustattu teksti, joka tulee näkyviin klikkaamalla. Plugin tenttaa käyttäjältä tietoa siitä aiheesta, jota hän on juuri klikannut. Valitse siis klikattavat sanat tai lauseet huolellisesti, jotta ne ovat merkityksellisiä mysteerin kannalta ja käyttävät valittua interaktiivista elementtiä hyödykseen strategisesti oikealla tavalla. Jos luot kysymyksen (plugin), tee vain yksi kysymys ja tee siitä niin helppo, että siihen on mahdollista vastata document-tekstin perusteella. Kysymys ei saa sisältää tietoa, jota document-tekstissä ei ole kysymykseen mennessä kerrottu. Älä ole myöskään liian tekninen kysymyksessä vaan kysy selkeitä asioita, jotka on mahdollista muistaa helposti.
      
      6. Ratkaisuteksti, joka tiivistää mysteerin ratkaisun
      
      Muotoile vastauksesi JSON-objektina seuraavalla rakenteella:
      {
        "title": "merkkijono",
        "mystery": "merkkijono",
        "document": [
          {
            "type": "paragraph",
            "text": "tutki lähdemateriaalia file_search-työkalulla, poimi keskeinen teema ja kirjoita tietoa hyväksi käyttävä etsivä-tyylinen tapauskertomus"
          }
        ],
        "clues": {
          "clue_id1": "johtolangan teksti 1",
          "clue_id2": "johtolangan teksti 2"
        },
        // Array of interactive elements. Leave empty for now.
        "interactiveElements": [
        {
            id: string;
            type: string; // 'examine' | 'censored' | 'plugin'
            word: string; // word or phrase to be interactive. Important: This word or sentence MUST exist in the document array text exactly as it is.
            clueId?: string; // Which clue this element reveals?
			pluginType?: PluginType; // If type is PLUGIN, this indicates the plugin type. Currently only choice is: 120 - use this, if you create pluginData.
            pluginData?: object; // If type is PLUGIN, this contains an object of plugin data. The object structure is:
            ** type: PluginType; // This has to be 120 for this plugin
			
			** questions: MultiChoiceQuestion[] = []; // Array of questions for the trivia game with the following structure:
			**** questionText: string;
			**** imageId?: number;
			
			**** answers: MultiChoiceAnswer[] // Answers for the question with the following structure:
			****** text: string; // Answer text
			****** isCorrect: boolean; // Boolean to indicate if this is the correct answer. More than one can be correct, but usually only one.
			
			**** explanationText: string; // Text shown when answered incorrectly
			**** correctExplanationText?: string; // Optional text shown when answered correctly
			
			** displayMode: 'list' | 'sequential' = 'sequential'; // Whether to display all questions at once or one after another. Use 'sequential' for this plugin.
			** selectedInterfaceId: number = 1; // Always use 1
	    }],
        "solutionClueCount": 5,
        "solutionText": "Lopullinen ratkaisuteksti, joka selittää mysteerin ratkaisun"
      }
        
      <all-clues>${parameters['existingClues'] || {}}</all-clues>
      
      Varmista, että mukana on:
      - Useita interaktiivisia elementtejä käyttäen yllä annettuja vaihtoehtoja
      - Sekoitus sensuroitua tekstiä, joka paljastaa johtolankoja napsautettaessa
      - Kysymyksiä, jotka testaavat pelaajan ymmärrystä tapauksesta
      - Johdonmukainen mysteeri, joka on opettavainen ja mukaansatempaava
      
      Interaktiivisten elementtien tulee olla oikein muotoiltuja asianmukaisilla HTML-attribuuteilla.
      
      ${targetLanguagePrompt}
      ${targetDifficultyPrompt}
      `;
		}

		return { system: systemPrompt, user: userPrompt };
	}

	/**
	 * Process AI response into PluginDetectiveCaseStudyGameData
	 * @param response The AI service response
	 * @param parameters Original generation parameters
	 * @returns Generation result with game data or error
	 */
	protected processResponseCore(response: any, parameters: any): GenerationResult {
		try {
			const json = response.json;

			if (!json) {
				return {
					success: false,
					error: 'Invalid response format: no JSON found',
					rawResponse: response.rawResponse,
				};
			}

			// Validate required fields
			if (!json.title || !json.mystery) {
				return {
					success: false,
					error: 'Missing required fields: title or mystery',
					rawResponse: response.rawResponse,
				};
			}

			// Create game data
			const gameData = new PluginDetectiveCaseStudyGameData();

			// Set basic properties
			gameData.title = json.title || 'Mysteeritapaus';
			gameData.mystery =
				json.mystery || 'Tutki johtolankoja ratkaistaksesi mysteerin.';

			// Process document paragraphs
			if (json.document && Array.isArray(json.document)) {
				gameData.document = json.document.map(
					(p: any) =>
						({
							type: 'paragraph',
							text: p.text || '',
						}) as DetectiveParagraph,
				);
			} else {
				gameData.document = [];
			}

			// Set clues
			gameData.clues = json.clues || {};

			// Set solution properties
			gameData.solutionClueCount = json.solutionClueCount || 5;
			gameData.solutionText =
				json.solutionText || 'Mysteeri on ratkaistu.';

			// Process interactive elements if they exist
			if (
				json.interactiveElements &&
				Array.isArray(json.interactiveElements)
			) {
				gameData.interactiveElements =
					json.interactiveElements as InteractiveElement[];
			} else {
				gameData.interactiveElements = [];
			}

			return {
				success: true,
				gameData,
				rawResponse: response.rawResponse,
			};
		} catch (error) {
			return {
				success: false,
				error: `Response processing failed: ${error}`,
				rawResponse: response.rawResponse,
			};
		}
	}

	/**
	 * Validate detective case study specific request
	 */
	validateRequest(request: GenerationRequest): ValidationResult {
		const hasSourceMaterial =
			request.sourceMaterial &&
			request.sourceMaterial.fileHandles.length > 0;
		const hasTopic =
			request.parameters['caseTitle'] &&
			request.parameters['caseTitle'].trim();

		if (!hasSourceMaterial && !hasTopic) {
			return {
				valid: false,
				error: 'Syötä tapauksen otsikko tai valitse lähdemateriaali',
			};
		}

		return { valid: true };
	}

	/**
	 * Get default parameters for detective case study
	 */
	getDefaultParameters(): any {
		return {
			caseTitle: '',
			clearPrevious: false,
			solutionClueCount: 5,
		};
	}

	/**
	 * Merge new content with existing game data based on parameters
	 * @param existingGameData Current game data
	 * @param newGameData Generated game data
	 * @param clearPrevious Whether to clear existing content
	 * @returns Updated game data
	 */
	override mergeContent(
		existingGameData: PluginDetectiveCaseStudyGameData,
		newGameData: PluginDetectiveCaseStudyGameData,
		clearPrevious: boolean,
	): PluginDetectiveCaseStudyGameData {
		if (clearPrevious) {
			// Replace everything with new data
			return newGameData;
		} else {
			// Merge new content with existing
			// For detective case study, we typically replace everything as it's a complete narrative
			// But we could potentially merge clues and interactive elements
			existingGameData.title = newGameData.title;
			existingGameData.mystery = newGameData.mystery;
			existingGameData.document = newGameData.document;
			existingGameData.solutionText = newGameData.solutionText;
			existingGameData.solutionClueCount = newGameData.solutionClueCount;

			// Merge clues (keeping existing ones and adding new ones)
			existingGameData.clues = {
				...existingGameData.clues,
				...newGameData.clues,
			};

			// Merge interactive elements
			existingGameData.interactiveElements = [
				...(existingGameData.interactiveElements || []),
				...(newGameData.interactiveElements || []),
			];
		}

		return existingGameData;
	}
}
